import {
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  GridProps,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { classes } from "portal/theme/theme";
import { ClassValue } from "clsx";
import { numberOrEmptyString } from "portal/utils/forms";
import { range } from "portal/utils/arrays";
import { TFunction } from "i18next";
import {
  TVEProfile,
  TVERowProfile,
} from "protos/target_velocity_estimator/target_velocity_estimator";
import { useTranslation } from "react-i18next";
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useState,
} from "react";

const DIVIDER_HEIGHT = "h-1";
const SECTION_WIDTH = "w-3/4";
const SECTION_WIDTH_FULL = "w-4/4";
const SECTION_HEIGHT = "h-12";

const SPEEDOMETER_LINES = {
  RED: "bg-red-500",
  BLUE: "bg-blue-500",
  GREEN: "bg-green-500",
  YELLOW: "bg-yellow-500",
};

const getFormula = (
  t: TFunction
): Array<{
  label: string;
  description: string;
  key: keyof TVEProfile;
  min?: number;
  max?: number;
  step?: number;
  color: string;
}> => [
  {
    label: t("components.velocityEstimator.configs.primaryRange.label"),
    key: "primaryRange",
    description: t(
      "components.velocityEstimator.configs.primaryRange.description"
    ),
    step: 1,
    max: 100,
    color: "border-green-500 border-2",
  },
  {
    label: t("components.velocityEstimator.configs.secondaryRange.label"),
    key: "secondaryRange",
    description: t(
      "components.velocityEstimator.configs.secondaryRange.description"
    ),
    step: 1,
    max: 100,
    color: "border-yellow-500 border-2",
  },
];

const getRowFormula = (
  t: TFunction
): Array<{
  label: string;
  description: string;
  key: keyof TVERowProfile;
  min?: number;
  max?: number;
  step?: number;
  color: string;
}> => [
  {
    key: "secondaryKillRate",
    label: t("components.velocityEstimator.configs.secondaryKillRate.label"),
    description: t(
      "components.velocityEstimator.configs.secondaryKillRate.description"
    ),
    step: 1,
    max: 100,
    color: "border-red-500 border-2",
  },
  {
    key: "primaryKillRate",
    label: t("components.velocityEstimator.configs.primaryKillRate.label"),
    description: t(
      "components.velocityEstimator.configs.primaryKillRate.description"
    ),
    step: 1,
    max: 100,
    color: "border-blue-500 border-2",
  },
];

export const GRID_ROW: GridProps = {
  spacing: 1,
  container: true,
  className: "w-full items-center justify-center",
};

interface Props {
  tveProfile: TVEProfile;
  areRowsSynced: boolean;
  readOnly?: boolean;
  onToggleSyncAllRows: () => void;
  setUpdatedTve: Dispatch<SetStateAction<TVEProfile | undefined>>;
}

// supports dynamic number of rows for reaper (slayer should always have 3)
const getRowKeys = (
  rows: { [key: number]: TVERowProfile } | undefined
): number[] => {
  const keys = Object.keys(rows ?? {})
    .map(Number)
    .filter((k) => !Number.isNaN(k))
    .sort((a, b) => a - b);
  return keys.length > 0 ? keys : [1];
};

export const FormulaCard: FunctionComponent<Props> = ({
  tveProfile,
  areRowsSynced,
  readOnly = false,
  onToggleSyncAllRows,
  setUpdatedTve,
}) => {
  const { t } = useTranslation();
  const rowKeys = getRowKeys(tveProfile.rows);
  const [focusedTveParameter, setFocusedTveParameter] = useState<
    keyof TVEProfile | undefined
  >();
  const getVisualizationClasses = (
    profileKey: keyof TVEProfile
  ): ClassValue[] => {
    return [
      focusedTveParameter === profileKey ? SECTION_WIDTH_FULL : SECTION_WIDTH,
      focusedTveParameter === profileKey ? "animate-pulse" : "",
    ];
  };
  return (
    <Card className="p-8 my-4 flex-shrink-0 flex-grow">
      {/* Header */}
      <Typography variant="h6">
        {t("components.velocityEstimator.configs.card.formulaTitle")}
      </Typography>
      {/* Visualization */}
      <div className="flex justify-center">
        <div className="relative w-96 h-64">
          <div className="absolute flex flex-col w-full h-full justify-center">
            <div
              className={classes(
                SPEEDOMETER_LINES.RED,
                DIVIDER_HEIGHT,
                ...getVisualizationClasses(
                  "secondaryKillRate" as keyof TVEProfile
                )
              )}
            />
            <div
              className={classes(
                "bg-gradient-to-r from-yellow-500",
                SECTION_HEIGHT,
                SECTION_WIDTH
              )}
            />
            <div
              className={classes(
                SPEEDOMETER_LINES.BLUE,
                DIVIDER_HEIGHT,
                ...getVisualizationClasses(
                  "primaryKillRate" as keyof TVEProfile
                )
              )}
            />
            <div className="flex">
              <div
                className={classes(
                  "bg-gradient-to-r from-green-500 flex-shrink",
                  SECTION_HEIGHT,
                  SECTION_WIDTH
                )}
              />
              <div className="flex flex-col justify-center font-bold text-green-500 whitespace-nowrap">
                {t("components.velocityEstimator.visualization.targetSpeed")}
              </div>
            </div>
            <div
              className={classes(
                SPEEDOMETER_LINES.GREEN,
                DIVIDER_HEIGHT,
                ...getVisualizationClasses("primaryRange" as keyof TVEProfile)
              )}
            />
            <div
              className={classes(
                "bg-gradient-to-r from-yellow-500",
                SECTION_HEIGHT,
                SECTION_WIDTH
              )}
            />
            <div
              className={classes(
                SPEEDOMETER_LINES.YELLOW,
                DIVIDER_HEIGHT,
                ...getVisualizationClasses("secondaryRange" as keyof TVEProfile)
              )}
            />
          </div>
          <div className="absolute bg-current w-1 h-full" />
          <div className="absolute flex flex-col w-full h-full justify-evenly">
            {range(10).map((index) => (
              <div
                key={index}
                className={classes("bg-current h-1", {
                  "w-6": index % 2 === 0,
                  "w-12": index % 2 === 1,
                })}
              />
            ))}
          </div>
        </div>
      </div>
      {/* Form */}
      <div className="w-full flex flex-col justify-center items-center gap-8">
        {/* Title and sync */}
        <Grid {...GRID_ROW}>
          <Grid
            className="hidden md:block"
            size={{
              xs: 0,
              md: 2,
            }}
          />
          {!areRowsSynced &&
            rowKeys.map((rowKey) => (
              <Grid
                key={rowKey}
                className="order-2 flex flex-row justify-center md:order-1"
                size={{
                  xs: Math.max(2, Math.floor(12 / rowKeys.length)),
                  md: 2,
                }}
              >
                {t("components.velocityEstimator.configs.rows.rowN", {
                  n: rowKey,
                })}
              </Grid>
            ))}
          {areRowsSynced && (
            <Grid
              className="order-2 flex flex-row justify-center md:order-1"
              size={{
                xs: 12,
                md: 2,
              }}
            >
              {t("components.velocityEstimator.configs.rows.allRows")}
            </Grid>
          )}
          <Grid
            className="order-1 text-sm text-center md:order-2 md:text-left"
            size={{
              xs: 12,
              md: areRowsSynced ? 8 : Math.max(4, 12 - 2 - rowKeys.length * 2),
            }}
          >
            {readOnly ? (
              ""
            ) : (
              <FormControlLabel
                control={
                  <Checkbox
                    color="info"
                    checked={areRowsSynced}
                    onChange={() => onToggleSyncAllRows()}
                  />
                }
                label={t("components.velocityEstimator.configs.sync")}
              />
            )}
          </Grid>
        </Grid>
        {getRowFormula(t).map(
          ({
            label,
            description,
            key,
            min = 0,
            max = 100,
            step = 1,
            color,
          }) => (
            <Grid {...GRID_ROW} key={key}>
              <Grid
                className="order-1 font-bold"
                size={{
                  xs: 12,
                  md: 2,
                }}
              >
                {label}
              </Grid>
              {(areRowsSynced ? rowKeys.slice(0, 1) : rowKeys).map((rowKey) => {
                const rowCount = rowKeys.length;
                const xsSize = areRowsSynced
                  ? 12
                  : Math.max(2, Math.floor(12 / rowCount));
                return (
                  <Grid
                    key={rowKey + key}
                    className="order-3 flex flex-col items-center md:order-2 md:flex-row"
                    size={{
                      xs: xsSize,
                      md: 2,
                    }}
                  >
                    <TextField
                      size="small"
                      onFocus={() =>
                        setFocusedTveParameter(key as keyof TVEProfile)
                      }
                      onBlur={() => setFocusedTveParameter(undefined)}
                      disabled={readOnly}
                      type="number"
                      value={(() => {
                        const row = tveProfile.rows[rowKey];
                        const value = row
                          ? (row as TVERowProfile)[key]
                          : undefined;
                        return typeof value === "number"
                          ? Math.round(value * 100)
                          : value;
                      })()}
                      className="w-full text-center"
                      onChange={(event) => {
                        const { rows } = tveProfile;
                        const value = numberOrEmptyString(event.target.value);
                        const tveRowProfileValue = TVERowProfile.fromPartial({
                          ...rows[rowKey],
                          [key]:
                            typeof value === "number" ? value / 100 : value,
                        });
                        const syncedRows = Object.fromEntries(
                          rowKeys.map((k) => [k, tveRowProfileValue])
                        ) as { [key: number]: TVERowProfile };
                        setUpdatedTve({
                          ...tveProfile,
                          rows: areRowsSynced
                            ? syncedRows
                            : {
                                ...rows,
                                [rowKey]: tveRowProfileValue,
                              },
                        });
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                          classes: {
                            notchedOutline: color,
                          },
                        },

                        htmlInput: { min, max, step },
                      }}
                    />
                  </Grid>
                );
              })}

              <Grid
                className="order-2 text-sm md:order-3"
                size={{
                  xs: 12,
                  md: areRowsSynced
                    ? 8
                    : Math.max(4, 12 - 2 - rowKeys.length * 2),
                }}
              >
                {description}
              </Grid>
            </Grid>
          )
        )}

        {getFormula(t).map(
          ({
            label,
            description,
            key,
            min = 0,
            max = Infinity,
            step = 1,
            color,
          }) => (
            <Grid {...GRID_ROW} key={key}>
              <Grid
                className="order-1 font-bold"
                size={{
                  xs: 12,
                  md: 2,
                }}
              >
                {label}
              </Grid>
              <Grid
                className="order-3 flex flex-col items-center md:order-2 md:flex-row"
                size={{
                  xs: 12,
                  md: 2,
                }}
              >
                <TextField
                  size="small"
                  onFocus={() =>
                    setFocusedTveParameter(key as keyof TVEProfile)
                  }
                  onBlur={() => setFocusedTveParameter(undefined)}
                  disabled={readOnly}
                  type="number"
                  value={(() => {
                    const value = tveProfile[key];
                    return typeof value === "number"
                      ? Math.round(value * 100)
                      : value;
                  })()}
                  className="w-full text-center"
                  onChange={(event) => {
                    const value = numberOrEmptyString(event.target.value);
                    setUpdatedTve({
                      ...tveProfile,
                      [key]: typeof value === "number" ? value / 100 : value,
                    });
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                      classes: {
                        notchedOutline: color,
                      },
                    },

                    htmlInput: { min, max, step },
                  }}
                />
              </Grid>
              <Grid
                className="order-2 text-sm md:order-3"
                size={{
                  xs: 12,
                  md: 8,
                }}
              >
                {description}
              </Grid>
            </Grid>
          )
        )}
      </div>
    </Card>
  );
};
